import {
  Building,
  Calendar,
  FileText,
  Phone,
  Quote,
  Users,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CoverageExtractionResultSchema } from '@/services/types/coverage-types';

interface ExtractionDetailsProps {
  extraction: CoverageExtractionResultSchema | null;
}

export const ExtractionDetails = ({ extraction }: ExtractionDetailsProps) => {
  if (!extraction) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p>No extraction data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Confidence and Reasoning */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Extraction Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Confidence Score:</span>
              <span className="font-medium">
                {(extraction.confidence * 100).toFixed(1)}%
              </span>
            </div>
            <Progress value={extraction.confidence * 100} className="h-2" />
          </div>

          {extraction.reasoning && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Reasoning:</h4>
              <p className="text-sm bg-gray-50 p-3 rounded-md">
                {extraction.reasoning}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Excerpts */}
      {extraction.excerpts && extraction.excerpts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Quote className="h-5 w-5" />
              Supporting Excerpts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {extraction.excerpts.map((excerpt, index) => (
                <div
                  key={index}
                  className="border-l-4 border-blue-200 pl-4 py-2 bg-blue-50 rounded-r-md"
                >
                  <p className="text-sm italic">&quot;{excerpt}&quot;</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coverage Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Basic Coverage Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Coverage Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {extraction.category && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Category:</span>
                <Badge variant="secondary">
                  {extraction.category.replace('_', ' ')}
                </Badge>
              </div>
            )}

            {extraction.policy_type && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Type Detail:</span>
                <span className="text-sm font-medium">
                  {extraction.policy_type}
                </span>
              </div>
            )}

            {extraction.policy_identifiers &&
              extraction.policy_identifiers.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Policy ID:</span>
                  <div className="text-right">
                    {extraction.policy_identifiers.map((identifier, index) => (
                      <div key={index} className="text-sm">
                        {identifier.identifier_type}:{' '}
                        {identifier.identifier_value}
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>

        {/* Carrier Information */}
        {extraction.carrier && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building className="h-5 w-5" />
                Carrier Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {extraction.carrier.name && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Name:</span>
                  <span className="text-sm font-medium">
                    {extraction.carrier.name}
                  </span>
                </div>
              )}

              {extraction.carrier.handles &&
                extraction.carrier.handles.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm text-gray-600">
                      Contact Methods:
                    </span>
                    <div className="space-y-1">
                      {extraction.carrier.handles.map((handle, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 text-sm"
                        >
                          {handle.handle_type === 'PHONE' && (
                            <Phone className="h-3 w-3" />
                          )}
                          <span className="text-gray-600">
                            {handle.handle_type}:
                          </span>
                          <span className="font-medium">{handle.value}</span>
                          {handle.name && (
                            <span className="text-gray-500">
                              ({handle.name})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Policy Details and Coverage Limits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Policy Details */}
        {extraction.policy_details && extraction.policy_details.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Policy Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {extraction.policy_details.map((detail, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-600">{detail.key}:</span>
                    <span className="font-medium">{detail.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Dates and People */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Dates & People
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {extraction.named_insured &&
                extraction.named_insured.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      Insured Names:
                    </span>
                    <div className="space-y-1">
                      {extraction.named_insured.map((name, index) => (
                        <span key={index} className="text-sm font-medium block">
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Adjusters and Subrogators */}
          {(extraction.adjusters || extraction.subrogator) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Claims Personnel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {extraction.adjusters && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      Assigned Adjuster
                    </h4>
                    {extraction.adjusters.map((adjuster, index) => (
                      <div key={index} className="border rounded p-2">
                        {adjuster.name && (
                          <div className="text-sm font-medium">
                            {adjuster.name}
                          </div>
                        )}
                        {adjuster.benefit_type && (
                          <div className="text-sm text-gray-600">
                            Assigned to: {adjuster.benefit_type}
                          </div>
                        )}
                        {adjuster.handles && adjuster.handles.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {adjuster.handles.map((handle, handleIndex) => (
                              <div
                                key={handleIndex}
                                className="text-xs text-gray-600"
                              >
                                {handle.handle_type}: {handle.value}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {extraction.subrogator && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      Subrogator
                    </h4>
                    {extraction.subrogator.name && (
                      <p className="text-sm font-medium">
                        {extraction.subrogator.name}
                      </p>
                    )}
                    {extraction.subrogator.handles && (
                      <div className="space-y-1">
                        {extraction.subrogator.handles.map((handle, index) => (
                          <div key={index} className="text-sm text-gray-600">
                            {handle.handle_type}: {handle.value}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
